import { useEffect } from "react";
import { logEvent } from "../../../infra/firebase/analytics";
import { AnalyticsEvent, AnalyticsParam } from "../../../domain/analytics";
import { PageMeta } from "../../seo/PageMeta";
import styles from "./PrivacyPolicyPage.module.css";

export default function PrivacyPolicyPage() {
  useEffect(() => {
    logEvent(AnalyticsEvent.screenView, {
      [AnalyticsParam.screenName]: "privacy_policy",
    });
  }, []);

  // This route is lazily loaded, so the browser's native hash scroll fires
  // before the content has mounted. Scroll to the anchored section ourselves
  // once it's in the DOM (e.g. /privacy-policy#delete-account).
  useEffect(() => {
    const { hash } = window.location;
    if (!hash) return;
    const el = document.getElementById(decodeURIComponent(hash.slice(1)));
    if (el) {
      requestAnimationFrame(() =>
        el.scrollIntoView({ behavior: "auto", block: "start" })
      );
    }
  }, []);

  return (
    <div className={styles.page}>
      <PageMeta
        title="Privacy Policy"
        description="Breath Lab's privacy policy — how we collect, use, and protect your data."
        path="/privacy-policy"
      />
      <div className={styles.content}>
        <h1 className={styles.title}>Privacy Policy</h1>
        <p className={styles.lastUpdated}>Last updated: May 14, 2026</p>

        <p className={styles.intro}>
          Sequence Studios Ltd. (Company No. 15455683), a company registered
          in the United Kingdom (&ldquo;Sequence Studios&rdquo;,
          &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;), operates
          the Breath Lab mobile application and website (the
          &ldquo;Service&rdquo;). This Privacy Policy explains how we collect,
          use, disclose, and protect your information when you use our
          Service. It applies in accordance with the UK General Data
          Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          Sequence Studios is the data controller for the personal data
          described in this policy.
        </p>

        <section className={styles.section} id="information-we-collect">
          <h2 className={styles.sectionTitle}>1. Information We Collect</h2>
          <h3 className={styles.subTitle}>Account Information</h3>
          <p>
            When you create an account, we collect your email address and, if
            you sign in with Google, your name and profile picture. We do not
            collect your password directly — it is managed securely by Firebase
            Authentication.
          </p>
          <h3 className={styles.subTitle}>Usage Data</h3>
          <p>
            We collect information about how you use the Service, including the
            breathwork sessions you complete, session duration, the number of
            rounds performed, and any feelings you log before or after a
            session. We treat feelings data with care; it is used solely to
            personalise your experience and is not shared with third parties who
            act as independent data controllers for any other purpose.
          </p>
          <h3 className={styles.subTitle}>Device Information</h3>
          <p>
            We may collect information about your device, including device type,
            operating system version, and unique device identifiers, for
            analytics and troubleshooting purposes.
          </p>
          <h3 className={styles.subTitle}>Error and Crash Reports</h3>
          <p>
            On the web app, we use Sentry to capture error reports and crash
            diagnostics. On the mobile app, we use Firebase Crashlytics. Both
            services may collect device type, operating system version, and a
            stack trace of the error. No personally identifiable information is
            intentionally included in these reports.
          </p>
          <h3 className={styles.subTitle}>Cookies and Similar Technologies</h3>
          <p>
            We use cookies and similar tracking technologies on our website in
            accordance with the Privacy and Electronic Communications
            Regulations 2003 (PECR). We use two categories of cookies:
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Essential cookies:</strong> Necessary for the Service to
              function (e.g. authentication session cookies). These cannot be
              disabled.
            </li>
            <li>
              <strong>Analytics cookies:</strong> Set by Google Analytics /
              Firebase Analytics to help us understand how users interact with
              the Service. These are only set with your consent.
            </li>
          </ul>
          <p>
            Before and unless you accept analytics cookies, we use Google
            Consent Mode to collect cookieless analytics. This measures
            aggregate usage and the source of website traffic without storing
            cookies on your device and without identifying you. If you accept
            analytics cookies, we additionally use cookie-based analytics as
            described above.
          </p>
          <p>
            You can manage your cookie preferences at any time via the consent
            banner. You can also block or delete cookies through your browser
            settings; note that disabling analytics cookies does not affect your
            use of the Service. The mobile app does not use cookies for
            analytics; see <em>Legal Basis for Processing</em> for how analytics
            work in the app.
          </p>
        </section>

        <section className={styles.section} id="legal-basis">
          <h2 className={styles.sectionTitle}>2. Legal Basis for Processing</h2>
          <p>
            Under UK GDPR, we must have a lawful basis for processing your
            personal data. The table below sets out the basis we rely on for
            each type of processing:
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Account and session data</strong> — performance of a
              contract (providing the Service you have signed up for)
            </li>
            <li>
              <strong>Feelings and mood logs</strong> — your consent, given
              when you choose to log feelings within the app. We treat
              feelings data with care given its personal nature and process
              it only for the purposes set out in this policy.
            </li>
            <li>
              <strong>Analytics and usage patterns (website)</strong> — your
              consent, given via the cookie consent banner
            </li>
            <li>
              <strong>Analytics and usage patterns (mobile app)</strong> — our
              legitimate interest in understanding how the app is used so we can
              improve it. The mobile app uses no cookies and no advertising
              identifiers; analytics are tied to a pseudonymous app-instance
              identifier and are not used to track you across other apps or
              services. You can opt out at any time in the app&rsquo;s Profile
              settings.
            </li>
            <li>
              <strong>Push notifications</strong> — your consent, given when you
              opt in to notifications through your device or browser settings
            </li>
            <li>
              <strong>Error and crash diagnostics (Sentry, Firebase
              Crashlytics)</strong> — our legitimate interest in maintaining a
              stable and reliable Service
            </li>
            <li>
              <strong>Fraud prevention and security</strong> — our legitimate
              interest in protecting the Service and its users
            </li>
            <li>
              <strong>Legal compliance</strong> — compliance with a legal
              obligation
            </li>
          </ul>
        </section>

        <section className={styles.section} id="how-we-use-information">
          <h2 className={styles.sectionTitle}>
            3. How We Use Your Information
          </h2>
          <p>We use the information we collect to:</p>
          <ul className={styles.list}>
            <li>Provide, maintain, and improve the Service</li>
            <li>Personalise your experience and track your progress</li>
            <li>
              Send you notifications related to the Service, where you have
              opted in
            </li>
            <li>Analyse usage patterns to improve features and content</li>
            <li>Diagnose and fix technical errors in the Service</li>
            <li>Detect and prevent fraudulent or unauthorised activity</li>
            <li>Comply with legal obligations</li>
          </ul>
          <p>
            You can withdraw consent for notifications at any time through your
            device or browser notification settings.
          </p>
        </section>

        <section className={styles.section} id="data-retention">
          <h2 className={styles.sectionTitle}>4. Data Storage and Retention</h2>
          <p>
            Your data is stored on Google Firebase infrastructure (Firestore,
            Firebase Authentication, Firebase Remote Config), with servers
            located in the European Economic Area or the United States (see
            Section 7 for international transfer details).
          </p>
          <p>We retain your data as follows:</p>
          <ul className={styles.list}>
            <li>
              <strong>Account data</strong> (email, profile): retained for the
              lifetime of your account, then deleted within 30 days of account
              deletion (the delay reflects Firebase Authentication&rsquo;s
              internal deletion process)
            </li>
            <li>
              <strong>Session and progress records</strong>: retained for the
              lifetime of your account, then deleted immediately on account
              deletion
            </li>
            <li>
              <strong>Feelings logs</strong>: retained for the lifetime of your
              account; deleted immediately on account deletion. You may also
              request deletion independently of your account at any time.
            </li>
            <li>
              <strong>Analytics data</strong>: retained in aggregated form for
              up to 26 months by Google Analytics, per Google&rsquo;s data
              retention settings
            </li>
            <li>
              <strong>Error reports (Sentry)</strong>: retained for 90 days
            </li>
          </ul>
          <p>
            You may request deletion of your account and all associated data at
            any time by contacting us at{" "}
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>
            .
          </p>
        </section>

        <section className={styles.section} id="delete-account">
          <h2 className={styles.sectionTitle}>
            5. How to Delete Your Account
          </h2>
          <p>
            You can delete your account, and all personal data associated with
            it, at any time. Account deletion is permanent and cannot be undone.
          </p>
          <h3 className={styles.subTitle}>Delete from the web app</h3>
          <p>
            Sign in on our website, open your <strong>Profile</strong> page, and
            select <strong>Delete account</strong>. You will be asked to confirm
            and, for your security, to re-authenticate before the deletion is
            processed.
          </p>
          <h3 className={styles.subTitle}>Delete from the mobile app</h3>
          <p>
            Open the app, go to the <strong>Profile</strong> tab, and tap{" "}
            <strong>Delete account</strong>. You will be asked to confirm and to
            re-authenticate before the deletion is processed.
          </p>
          <h3 className={styles.subTitle}>Delete by email</h3>
          <p>
            If you cannot access your account, you can request deletion by
            emailing{" "}
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>{" "}
            from the email address associated with your account. We will respond
            within one calendar month.
          </p>
          <h3 className={styles.subTitle}>What gets deleted</h3>
          <p>
            Deleting your account removes your profile, your breathwork session
            and progress records, and your feelings logs. Session, progress, and
            feelings data are deleted immediately; account data
            (email, profile) is fully removed within 30 days, reflecting
            Firebase Authentication&rsquo;s internal deletion process. See
            Section 4 (Data Storage and Retention) for full details.
          </p>
          <p>
            <strong>If you have an active subscription</strong>, cancel it
            before deleting your account to avoid being charged after deletion.
            Subscriptions purchased through the Apple App Store or Google Play
            must be cancelled through your Apple or Google account;
            subscriptions purchased on the web can be managed from your Profile
            page.
          </p>
        </section>

        <section className={styles.section} id="sharing-information">
          <h2 className={styles.sectionTitle}>6. Sharing Your Information</h2>
          <p>
            We do not sell, trade, or otherwise transfer your personally
            identifiable information to third parties, except as described
            below:
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Google Firebase</strong> (Firebase Authentication,
              Firestore, Remote Config, Firebase Analytics): used to operate and
              improve the Service. Google acts as a data processor on our
              behalf.
            </li>
            <li>
              <strong>Google Analytics / Firebase Analytics</strong>: used to
              analyse aggregated usage data. On the website, activated only with
              your consent via the cookie banner. In the mobile app, processed
              under our legitimate interest, with no cookies or advertising
              identifiers; you can opt out in the app&rsquo;s Profile settings.
            </li>
            <li>
              <strong>Sentry</strong>: used for error monitoring on the web app.
              Processes device and diagnostic data only.
            </li>
            <li>
              <strong>Firebase Crashlytics</strong>: used for crash reporting on
              the mobile app. Processes device and diagnostic data only. Google
              acts as a data processor on our behalf.
            </li>
            <li>
              <strong>Firebase Cloud Messaging (FCM)</strong>: used to deliver
              push notifications to your device when you have opted in. Google
              receives your device&rsquo;s push notification token to route
              messages. Google acts as a data processor on our behalf.
            </li>
            <li>
              <strong>Legal requirements:</strong> if required by law,
              regulation, or valid legal process
            </li>
            <li>
              <strong>Business transfers:</strong> in the event of a merger,
              acquisition, or sale of all or a portion of our assets, with
              notice to you where required by law
            </li>
          </ul>

          <h3 className={styles.subTitle}>Payment Processors</h3>
          <p>
            Subscription payments are processed by third-party providers, not by Breath Lab directly.
            We use:
          </p>
          <ul>
            <li>
              <strong>RevenueCat, Inc.</strong> — manages our subscription state across web, iOS, and Android.
              To do this, we share your account identifier, email address, and display name with
              RevenueCat. Their privacy policy:{' '}
              <a href="https://www.revenuecat.com/privacy" target="_blank" rel="noopener noreferrer">
                revenuecat.com/privacy
              </a>.
            </li>
            <li>
              <strong>Stripe, Inc.</strong> — processes credit/debit card payments on the web. Stripe is a
              PCI-DSS Level 1 certified payment processor; we never see your full card number. Their
              privacy policy:{' '}
              <a href="https://stripe.com/privacy" target="_blank" rel="noopener noreferrer">
                stripe.com/privacy
              </a>.
            </li>
            <li>
              <strong>Apple App Store</strong> and <strong>Google Play Store</strong> — process payments
              for subscriptions purchased through their respective mobile apps under their own terms and
              privacy policies.
            </li>
          </ul>
          <p>
            These processors receive the minimum information needed to complete a transaction (your
            payment instrument, billing country, the subscription product you selected). They do not
            receive your breathwork session history, mood data, or in-app activity.
          </p>
          <p>
            <strong>When payment information is collected.</strong> On the web, no payment information
            is collected during the 7-day free trial &mdash; your card details are only requested if you
            choose to convert to a paid subscription. On iOS and Android, the annual-plan free trial is
            operated as an introductory offer by the App Store or Google Play; a payment method on your
            Apple ID or Google account is therefore required by the store before the trial can begin.
            We do not directly receive or store your card details in either case.
          </p>

          <h3 className={styles.subTitle}>Bot Protection (Google reCAPTCHA)</h3>
          <p>
            We use Google reCAPTCHA to protect our sign-in, sign-up, and account recovery
            flows from automated abuse. reCAPTCHA analyses hardware and software information — including
            device data and behavioural signals — and sends it to Google for analysis. Use of reCAPTCHA
            is subject to{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
              Google's Privacy Policy
            </a>{' '}
            and{' '}
            <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">
              Google's Terms of Service
            </a>. We do not control and are not responsible for Google's processing of this data.
          </p>
        </section>

        <section className={styles.section} id="international-transfers">
          <h2 className={styles.sectionTitle}>
            7. International Data Transfers
          </h2>
          <p>
            Some of our service providers, including Google (Firebase, Google
            Analytics), Sentry, RevenueCat, and Stripe, may transfer or store
            your data outside the United Kingdom. Where this occurs, we ensure
            appropriate safeguards are in place:
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Google</strong> (Firebase Authentication, Firestore,
              Firebase Analytics, Firebase Crashlytics, Firebase Cloud
              Messaging): transfers to the US are covered by Google&rsquo;s UK
              International Data Transfer Addendum (UK IDTA) to the EU Standard
              Contractual Clauses, as recognised under the UK GDPR framework.
            </li>
            <li>
              <strong>Sentry</strong>: Sentry Inc. is certified under applicable
              data transfer mechanisms. Data may be processed in the United
              States.
            </li>
            <li>
              <strong>RevenueCat</strong>: RevenueCat Inc. is a US-based
              company. Data transfers are covered by Standard Contractual
              Clauses under the UK GDPR framework.
            </li>
            <li>
              <strong>Stripe</strong>: Stripe Inc. is a US-based company.
              Stripe is certified under applicable data transfer mechanisms,
              including Standard Contractual Clauses recognised under the UK
              GDPR framework.
            </li>
          </ul>
          <p>
            You can request a copy of the relevant transfer safeguards by
            contacting us at{" "}
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>
            .
          </p>
        </section>

        <section className={styles.section} id="childrens-privacy">
          <h2 className={styles.sectionTitle}>8. Children&rsquo;s Privacy</h2>
          <p>
            The Service is not directed to children under the age of 13. We do
            not knowingly collect personal information from children under 13.
            If you believe a child has provided us with personal information,
            please contact us so we can delete it.
          </p>
          <p>
            We are aware of the ICO&rsquo;s Age Appropriate Design Code
            (Children&rsquo;s Code). We apply data minimisation and
            privacy-by-default principles to our Service and do not use personal
            data of younger users for profiling or targeted content.
          </p>
        </section>

        <section className={styles.section} id="your-rights">
          <h2 className={styles.sectionTitle}>9. Your Rights Under UK GDPR</h2>
          <p>
            You have the following rights regarding your personal data. If you
            are in the United Kingdom, these apply under UK GDPR and the Data
            Protection Act 2018. If you are in the European Economic Area,
            equivalent rights apply under EU GDPR:
          </p>
          <ul className={styles.list}>
            <li>
              <strong>Right of access:</strong> request a copy of the personal
              data we hold about you
            </li>
            <li>
              <strong>Right to rectification:</strong> request correction of
              inaccurate or incomplete data
            </li>
            <li>
              <strong>Right to erasure:</strong> request deletion of your
              personal data where there is no compelling reason for us to
              continue processing it
            </li>
            <li>
              <strong>Right to restriction:</strong> request that we restrict
              processing of your data in certain circumstances
            </li>
            <li>
              <strong>Right to data portability:</strong> receive your data in a
              structured, commonly used format and transfer it to another
              controller
            </li>
            <li>
              <strong>Right to object:</strong> object to processing based on
              legitimate interests or for direct marketing
            </li>
            <li>
              <strong>Rights related to automated decision-making:</strong> we
              do not make decisions about you solely by automated means that
              produce legal or similarly significant effects
            </li>
          </ul>
          <p>
            To exercise any of these rights, contact us at{" "}
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>
            . We will respond within one calendar month. There is no charge for
            making a request.
          </p>
          <p>
            If you are unhappy with how we have handled your data, you have the
            right to lodge a complaint with the{" "}
            <strong>Information Commissioner&rsquo;s Office (ICO)</strong>:{" "}
            <a
              href="https://ico.org.uk"
              target="_blank"
              rel="noopener noreferrer"
            >
              ico.org.uk
            </a>{" "}
            — 0303 123 1113.
          </p>
          <p>
            If you are a California resident, you may also have rights under the
            California Consumer Privacy Act (CCPA), including the right to know
            what personal information we collect, the right to delete it, and
            the right to opt out of its sale. We do not sell personal
            information.
          </p>
        </section>

        <section className={styles.section} id="security">
          <h2 className={styles.sectionTitle}>10. Security</h2>
          <p>
            We take reasonable technical and organisational measures to protect
            your information against unauthorised access, alteration,
            disclosure, or destruction. These include use of Firebase&rsquo;s
            built-in security rules, encrypted transmission (HTTPS), and access
            controls limited to authorised personnel. However, no method of
            transmission over the internet or electronic storage is 100% secure.
          </p>
          <p>
            In the event of a personal data breach likely to result in a high
            risk to your rights and freedoms, we will notify you without undue
            delay and report the breach to the Information Commissioner&rsquo;s
            Office within 72 hours of becoming aware, as required by UK GDPR.
          </p>
        </section>

        <section className={styles.section} id="changes">
          <h2 className={styles.sectionTitle}>11. Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy from time to time. For material
            changes — changes that affect your rights or how we use your data in
            a significant way — we will notify you directly (via email or an
            in-app notice) and, where required by law, seek your renewed
            consent. Non-material changes will be indicated by updating the
            &ldquo;Last updated&rdquo; date at the top of this page.
          </p>
        </section>

        <section className={styles.section} id="contact">
          <h2 className={styles.sectionTitle}>12. Contact Us</h2>
          <p>
            If you have any questions about this Privacy Policy or our data
            practices, please contact us at:
          </p>
          <p className={styles.contactInfo}>
            Sequence Studios Ltd (Company No. 15455683)
            <br />
            71-75 Shelton Street, Covent Garden, London WC2H 9JQ
            <br />
            <a href="mailto:info@sequence-studios.com">
              info@sequence-studios.com
            </a>
            <br />
            ICO Registration Number: ZB798412
          </p>
          <p>
            We have not appointed a Data Protection Officer as we do not
            currently meet the criteria requiring mandatory appointment under
            Article 37 UK GDPR.
          </p>
        </section>
      </div>
    </div>
  );
}
